import React from 'react';
import type { GreenBeanProfile } from '../../types/greenBean';
import type { InventoryLot } from '../../types/inventory';
import { useLanguage } from '../../features/language/useLanguage';

interface GreenBeanItemInfoSectionProps {
  itemInfo?: Partial<GreenBeanProfile>;
  lotIdDisplay?: string;
  lotInfo?: InventoryLot;
}

export const GreenBeanItemInfoSection: React.FC<GreenBeanItemInfoSectionProps> = ({
  itemInfo,
  lotIdDisplay,
  lotInfo,
}) => {
  const { t } = useLanguage();
  if (!itemInfo?.item_code && !itemInfo?.item_name) {
    return null;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between border-b pb-2">
        <h2 className="text-lg font-medium text-gray-900">{t('greenBean.itemInfo.title')}</h2>
        {itemInfo?.work_order_id && (
          <a
            href={`/work-orders/${itemInfo.work_order_id}`}
            className="inline-flex items-center text-sm text-primary-600 hover:text-primary-800 hover:underline"
            data-test-id="green-bean-linked-work-order"
          >
            {t('greenBean.itemInfo.workOrder')} {itemInfo.work_order_id}
          </a>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('reports.export.columns.itemCode')}</label>
            <input
              type="text"
              value={itemInfo?.item_code || ''}
              disabled
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm border p-2 bg-gray-100 cursor-not-allowed"
              data-test-id="green-bean-item-code"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('inventory.history.lotNumber')}</label>
            {lotIdDisplay ? (
              <a
                href={`/inventory/lots/${lotIdDisplay}`}
                className="mt-1 block w-fit cursor-pointer rounded-md border hover:border-purple-600 border-purple-300 bg-purple-100 text-purple-800 px-3 py-1 text-sm hover:underline hover:bg-purple-300"
                data-test-id="green-bean-item-lot-number"
              >
                {lotInfo?.lotNumber || lotIdDisplay}
              </a>
            ) : (
              <div className="mt-1 block w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-400">
                {t('settingsMcp.values.none')}
              </div>
            )}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">{t('forms.customer.itemName')}</label>
          <input
            type="text"
            value={itemInfo?.item_name || ''}
            disabled
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm border p-2 bg-gray-100 cursor-not-allowed"
            data-test-id="green-bean-item-name"
          />
        </div>
      </div>
    </div>
  );
};
